import { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Ensure toast styles are imported
import { Database, WifiOff, Loader2, AlertTriangle, ServerCrash, Globe2 } from "lucide-react"; // Using lucide-react for modern icons
import { reportInitialSetupState, markInitialSetupCompleted } from "../helper/syncElectron";

const API_BASE_URL = (() => {
    const url = process.env.REACT_APP_BASE_URL;
    if (!url) return "";
    return url.startsWith("http") ? url : `http://${url}`;
})();

/**
 * Categorize a thrown error / non-OK response into one of:
 *   - "offline"   : no network connection at all
 *   - "network"   : DNS / connection refused / TLS / CORS preflight failure
 *   - "not-found" : reachable but endpoint / host returned 404 (wrong URL / host down at root)
 *   - "server"    : reachable, endpoint exists, but the server returned >=500 or threw
 *   - "client"    : reachable, but returned a 4xx other than 404 (bad request, etc.)
 *   - "unknown"   : anything else
 */
const categorizeError = (error, response) => {
    if (!navigator.onLine) {
        return {
            kind: "offline",
            title: "You are offline",
            message: "No internet connection detected on this device.",
            detail: "Connect to Wi-Fi or plug in a network cable, then click Retry."
        };
    }

    // fetch() only throws TypeError on network-layer failures
    if (error && (error.name === "TypeError" || error instanceof TypeError)) {
        const msg = (error.message || "").toLowerCase();
        if (msg.includes("failed to fetch")) {
            return {
                kind: "network",
                title: "Cannot reach server",
                message: "The remote server is not reachable.",
                detail: "Check the configured server URL, your firewall, or whether the server is running. The host may be down or blocking requests."
            };
        }
        if (msg.includes("load failed") || msg.includes("networkerror")) {
            return {
                kind: "network",
                title: "Network error",
                message: "A network error occurred while contacting the server.",
                detail: "Your connection may be unstable, or a CORS / TLS issue is blocking the request."
            };
        }
        return {
            kind: "network",
            title: "Connection failed",
            message: error.message || "Could not connect to the server.",
            detail: "Verify the server URL in your environment configuration."
        };
    }

    if (response) {
        if (response.status === 404) {
            return {
                kind: "not-found",
                title: "Server endpoint not found (404)",
                message: "The server responded, but the sync endpoint was not found.",
                detail: "This usually means the server URL is wrong, the API base path has changed, or the remote service is offline at the root."
            };
        }
        if (response.status >= 500) {
            return {
                kind: "server",
                title: `Server error (${response.status})`,
                message: "The server is reachable but failed to process the request.",
                detail: "Try again in a moment. If the problem persists, contact your administrator."
            };
        }
        if (response.status >= 400) {
            return {
                kind: "client",
                title: `Request rejected (${response.status})`,
                message: "The server rejected the initial-setup request.",
                detail: "This is unusual for first-time setup — please contact support."
            };
        }
    }

    return {
        kind: "unknown",
        title: "Setup failed",
        message: (error && error.message) || "An unexpected error occurred.",
        detail: "Try again. If the problem keeps happening, check the developer console for details."
    };
};

export default function OfflineInitialDataFetch() {
    const [isLoading, setIsLoading] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [errorState, setErrorState] = useState(null);
    const [stage, setStage] = useState(null); // null | "apply" | "commit"

    // Mirror setup-in-progress state into the Electron main process so the
    // window-close handler can prompt the user before letting them quit.
    useEffect(() => {
        reportInitialSetupState(isLoading);
        return () => reportInitialSetupState(false);
    }, [isLoading]);

    // Monitor internet connection status
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            setErrorState(prev =>
                prev && (prev.kind === "offline" || prev.kind === "network") ? null : prev
            );
        };
        const handleOffline = () => setIsOnline(false);

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    const fetchInitialData = async () => {
        if (!isOnline) {
            setErrorState(categorizeError(null, null));
            toast.error("You are offline. Connect to the internet and try again.");
            return;
        }

        setIsLoading(true);
        setErrorState(null);

        try {
            // -------- Stage 1: server applies snapshot locally --------
            // The server fetches the snapshot from the remote and bulkWrites
            // it into the local DB (Node-side, mongoose available). On
            // success it returns the snapshot's syncedAt.
            setStage("apply");
            console.log("[InitialSetup] STAGE 1 — server fetching + applying snapshot");
            let applyRes;
            try {
                applyRes = await fetch(`${API_BASE_URL}/sync/initial-setup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({}),
                });
            } catch (networkErr) {
                const cat = categorizeError(networkErr, null);
                console.error('[InitialSetup] STAGE 1 FAIL — network:', networkErr);
                setErrorState(cat);
                toast.error(`${cat.title}: ${cat.message}`);
                return;
            }

            if (!applyRes.ok) {
                let serverMessage = null;
                try {
                    const errBody = await applyRes.json();
                    serverMessage = errBody && (errBody.message || errBody.error);
                } catch (_) { }
                const cat = categorizeError(null, applyRes);
                if (serverMessage) cat.detail = `Server said: "${serverMessage}". ${cat.detail}`;
                console.error(`[InitialSetup] STAGE 1 FAIL — HTTP ${applyRes.status}:`, cat);
                setErrorState(cat);
                toast.error(`${cat.title}: ${cat.message}`);
                return;
            }

            const applyData = await applyRes.json();
            const { syncedAt } = applyData;
            console.log(`[InitialSetup] STAGE 1 OK — syncedAt=${syncedAt}`);

            // -------- Stage 2: commit — tell server to drop its change log --------
            setStage("commit");
            console.log("[InitialSetup] STAGE 2 — committing to remote (drop server change log)");
            let doneRes;
            try {
                doneRes = await fetch(`${API_BASE_URL}/sync/initial-done`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ syncedAt }),
                });
            } catch (commitErr) {
                console.warn('[InitialSetup] STAGE 2 WARN — commit failed (non-fatal):', commitErr);
                toast.warning('Local setup completed but the server could not be notified. Change logs will be cleaned up on the next sync.');
            }

            if (doneRes) {
                if (!doneRes.ok) {
                    const cat = categorizeError(null, doneRes);
                    console.warn(`[InitialSetup] STAGE 2 WARN — server returned HTTP ${doneRes.status}:`, cat);
                    toast.warning(`Server returned ${doneRes.status} during commit. Local data is OK; server cleanup will retry on next sync.`);
                } else {
                    const doneData = await doneRes.json();
                    console.log('[InitialSetup] STAGE 2 OK — server purged', doneData.deletedCount, 'change-log entries');
                }
            }

            markInitialSetupCompleted();
            toast.success('Database Setup completed successfully! Reloading...');
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            console.error('[InitialSetup] CRITICAL — unexpected failure during stage', stage, ':', error);
            toast.error(`Setup failed during ${stage || "init"}: ${error.message}`);
            setErrorState({
                kind: "unknown",
                title: `Setup failed during ${stage || "init"}`,
                message: error.message || "An unexpected error occurred.",
                detail: "Check the developer console for details. You can retry the setup."
            });
        } finally {
            setIsLoading(false);
            setStage(null);
        }
    };

    const errorIcon = (kind) => {
        switch (kind) {
            case "offline":
            case "network":
                return <WifiOff className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />;
            case "not-found":
                return <Globe2 className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />;
            case "server":
                return <ServerCrash className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />;
            default:
                return <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />;
        }
    };

    const stageLabel = () => {
        switch (stage) {
            case "apply": return "Fetching entities...";
            case "commit": return "Notifying server ...";
            default: return "Setting up database...";
        }
    };

    return (
        <div className="flex flex-col h-screen w-screen justify-center items-center bg-gray-50 p-4">
            <div className="max-w-md w-full bg-white rounded-xl shadow-md border border-gray-100 p-8 text-center space-y-6">

                {/* Visual Status Header */}
                <div className="flex justify-center">
                    {!isOnline ? (
                        <div className="p-4 bg-red-50 text-red-500 rounded-full animate-pulse">
                            <WifiOff className="w-12 h-12" />
                        </div>
                    ) : isLoading ? (
                        <div className="p-4 bg-blue-50 text-blue-600 rounded-full">
                            <Loader2 className="w-12 h-12 animate-spin" />
                        </div>
                    ) : (
                        <div className="p-4 bg-gray-50 text-gray-700 rounded-full">
                            <Database className="w-12 h-12" />
                        </div>
                    )}
                </div>

                {/* Text Content */}
                <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-gray-900">
                        {!isOnline ? "Connection Required" : "Local Database Setup"}
                    </h1>
                    <p className="text-gray-500 text-sm leading-relaxed">
                        {!isOnline
                            ? "You are currently offline. Please connect to the internet to fetch the initial admin configuration."
                            : "Currently, the database has no admins. Please fetch the initial data to safely create the root administrator account."
                        }
                    </p>
                </div>

                {/* Inline Offline Banner */}
                {!isOnline && (
                    <div className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg p-2.5">
                        ⚠️ Please check your Wi-Fi or network cable.
                    </div>
                )}

                {/* Inline Error Card */}
                {errorState && isOnline && (
                    <div
                        role="alert"
                        data-testid="initial-setup-error"
                        className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 p-3.5 rounded-lg text-left text-sm"
                    >
                        {errorIcon(errorState.kind)}
                        <div className="min-w-0 flex-1">
                            <span className="font-semibold block">{errorState.title}</span>
                            <span className="block mt-0.5">{errorState.message}</span>
                            {errorState.detail && (
                                <span className="block mt-1 text-xs text-red-700/80 break-words">
                                    {errorState.detail}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Action Button */}
                <button
                    onClick={fetchInitialData}
                    disabled={isLoading || !isOnline}
                    className={`w-full py-3 px-4 font-medium rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center space-x-2
                        ${!isOnline
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
                            : isLoading
                                ? "bg-blue-400 text-white cursor-wait"
                                : errorState
                                    ? "bg-red-600 text-white hover:bg-red-700 active:scale-[0.98]"
                                    : "bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]"
                        }`}
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{stageLabel()}</span>
                        </>
                    ) : !isOnline ? (
                        <span>Waiting for Internet...</span>
                    ) : errorState ? (
                        <span>Retry Setup</span>
                    ) : (
                        <span>Setup Database</span>
                    )}
                </button>
            </div>

            {/* Notification Toast Container */}
            <ToastContainer
                position="top-right"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
        </div>
    );
}